import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { formatMoney } from '@shopify/theme-currency';

import iconClose from '../../../assets/icon-cross.svg';

import CartItem from './cart-item';

const emptyCart = {
  item_count: 0,
  items: [],
  total_price: 0,
};

const Cart = ({ rootUrl, isCartPage }) => {
  const defaultCart = window.theme.cart ? window.theme.cart : emptyCart;

  const [cart, setCart] = useState(defaultCart);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const itemsCount = cart.item_count;
  const cartItems = cart.items;
  const total = cart.total_price;

  const cartLoadingMessage = window.theme.strings?.loadingMessage || 'Updating, please wait';
  const freeShippingBar = document.querySelector('free-shipping-bar');

  const updateCart = (newCart) => {
    setCart(newCart);
    const counters = document.querySelectorAll('[data-items-count]');
    counters.forEach((counter) => (counter.innerHTML = newCart.item_count));
    setIsLoading(false);

    // Klaviyo tracking for abandoned cart
    if (window._learnq) {
      let cartTracking = {
        total_price: newCart.total_price / 100,
        $value: newCart.total_price / 100,
        total_discount: newCart.total_discount,
        original_total_price: newCart.original_total_price / 100,
        items: newCart.items,
      };
      if (window.item !== 'undefined') {
        cartTracking = Object.assign(cart, window.item);
      }
      window._learnq.push(['track', 'Added to Cart', cartTracking]);
    }

    if (freeShippingBar) freeShippingBar.update();
  };

  const fetchCart = async () => {
    const url = `${rootUrl}/cart.js`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('Fetch cart response was not OK');
      }
      const json = await response.json();
      return json;
    } catch (err) {
      window.console.error(err);
    }
  };

  /**
   *
   * @typedef {Object} Item
   * @property {number} id - The Shopify variant ID.
   * @property {number} quantity - The quantity to add of this variant.
   * @property {Object.<string, string>} [properties] - Optional line item properties.
   * @property {Object} [variant] - Optional variant.
   */

  /**
   *
   * @param {...Item} items - Array of {@link Item} objects to add to cart.
   */
  const addLineItems = async (items) => {
    const data = {
      items: [items].flat(),
    };
    const url = `${rootUrl}/cart/add.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      setIsLoading(true);
      const response = await fetch(url, options);
      if (response.ok === false) {
        const errorResponse = await response.json();
        setErrorMessage(errorResponse.description);
        setIsLoading(false);
      }
      const cartContents = await fetchCart();
      updateCart(cartContents);
    } catch (err) {
      setIsLoading(false);
      window.console.error(err);
    }
  };

  const updateLineItems = async (updates) => {
    const data = {
      updates,
    };
    const url = `${rootUrl}/cart/update.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      setIsLoading(true);
      const response = await fetch(url, options);
      if (response.ok === false) {
        const errorResponse = await response.json();
        setErrorMessage(errorResponse.description);
        setIsLoading(false);
      }
      const cartContents = await response.json();
      updateCart(cartContents);
    } catch (err) {
      setIsLoading(false);
      window.console.error(err);
    }
  };

  const handleCartToggle = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (document.body.classList.contains('side-cart-is-visible')) {
      document.body.classList.remove('side-cart-is-visible');
      const cartOpenButton = document.querySelector('.header__btn--cart');
      if (cartOpenButton) {
        cartOpenButton.focus();
      }
    } else {
      document.body.classList.add('side-cart-is-visible');
      const cartCloseButton = document.querySelector('.cart__close');
      if (cartCloseButton) {
        cartCloseButton.focus();
      }
    }
  };

  /**
   *
   * @param {...Item} items - Array of {@link Item} objects to add to cart.
   * @param {boolean} openCart - Triggers the cart to open when item added if true
   */

  const handleAddToCartEvent = async (items, openCart = false) => {
    // Items could contain extra data like properties or variant info.
    // This reassignment strips out all but id, qty and properties to pass
    // to the addLineItems function for the cart.
    const lineItems = [items].flat().map((item) => {
      // ID and Qty will always be part of the array
      const newLineItem = {
        id: item.id,
        quantity: item.quantity,
      };
      // Only add properties if they exist
      if ('properties' in item) {
        newLineItem.properties = item.properties;
      }
      return newLineItem;
    });
    try {
      await addLineItems(lineItems);
      if (openCart && !isCartPage) {
        handleCartToggle();
      }
    } catch (err) {
      window.console.error(err);
    }
  };

  useEffect(() => {
    window.sideCart = {
      handleAddToCartEvent,
    };
    const cartToggles = document.querySelectorAll('[data-toggle-cart]');
    cartToggles.forEach((cartToggle) => cartToggle.addEventListener('click', handleCartToggle));
  }, []);

  return (
    <>
      <div className={`cart__loading ${isLoading ? 'is-shown' : ''}`}>
        <div className="cart__loader" />
        <p>
          <strong>{cartLoadingMessage}</strong>
        </p>
      </div>

      {errorMessage && (
        <div className="cart__error-message">
          {errorMessage}
          <button
            type="button"
            className="cart__dismiss-message btn-none"
            aria-label={window.theme.strings.dismissErrorMessage}
            onClick={() => setErrorMessage(null)}
          >
            <span dangerouslySetInnerHTML={{ __html: iconClose }} />
          </button>
        </div>
      )}

      {cartItems.length ? (
        <div className="cart-inner">
          <div className="row">
            <div className="cart table-wrap">
              <div className="col-md-12 col-lg-12 col-xl-12">
                <div className="cart-table-wrap">
                  <table className="data-table full cart-table">
                    <thead>
                      <tr>
                        <th className="hidden-phone"></th>
                        <th className="text-left">{window.theme.strings.cartTableHeaderProduct}</th>
                        <th className="text-left">{window.theme.strings.cartTableHeaderPrice}</th>
                        <th className="text-left">{window.theme.strings.cartTableHeaderQty}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.map((item) => <CartItem item={item} updateLineItems={updateLineItems} key={item.key} />)}
                    </tbody>
                    <tfoot>
                      {cart.cart_level_discount_applications &&
                        cart.cart_level_discount_applications.map((cartDiscount) => (
                          <tr>
                            <td colspan="4">
                              <div className="cart__subtotal" key={cartDiscount}>
                                <span className="cart__subtotal-discount">
                                  {window.theme.strings.discount}
                                  {cartDiscount.title}
                                </span>
                                <span
                                  className="cart__subtotal-value"
                                  dangerouslySetInnerHTML={{
                                    __html: '-' + formatMoney(cartDiscount.total_allocated_amount, window.theme.moneyFormat),
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      {!cart.cart_level_discount_applications.length && cart.total_discount > 0 && (
                        <tr>
                          <td colspan="4">
                            <div className="cart__subtotal">
                              <span className="cart__subtotal-discount">
                                {window.theme.strings.totalDiscounts}
                              </span>
                              <span
                                className="cart__subtotal-value"
                                dangerouslySetInnerHTML={{ __html: '-' + formatMoney(cart.total_discount, window.theme.moneyFormat) }}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td colspan="4">
                          <div className="cart__subtotal">
                            <span>{window.theme.strings.subtotal}</span>
                            <span
                              className="cart__subtotal-value"
                              dangerouslySetInnerHTML={{ __html: formatMoney(total, window.theme.moneyFormat) }}
                            />
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colspan="4">
                          <a href={`${rootUrl}/checkout`} className="cart__checkout-btn btn-button btn-update tiny" data-checkout-btn>
                            {window.theme.strings.checkout}
                          </a>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p>{window.theme.strings.cartEmpty}</p>
          <p dangerouslySetInnerHTML={{ __html: window.theme.strings.cartContinueBrowsing }} />
        </>
      )
      }
    </>
  );
};

Cart.propTypes = {
  rootUrl: PropTypes.string,
  isCartPage: PropTypes.bool,
};

export default Cart;
